import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../lib/store'
import { useBriefLoad } from '../lib/useBriefLoad'
import { signalCounts, verificationScore, isThinCredential } from '../lib/candidate'
import { canActOnPipeline } from '../lib/permissions'
import { timeAgo } from '../lib/format'
import type { Candidate } from '../lib/types'
import { Avatar, Button, Card, Select, Skeleton } from '../components/ui'
import { CredentialBadge, TierBadge } from '../components/Verification'
import { EmptyState } from '../components/EmptyState'
import { GhostMark } from '../components/GhostMark'
import { IconCheck, IconLock, IconPin, IconSearch, IconShield } from '../components/icons'
import './pages.css'

type SortKey = 'verification' | 'recent' | 'experience' | 'name'

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'verification', label: 'Sort: Verification strength' },
  { value: 'recent', label: 'Sort: Recently active' },
  { value: 'experience', label: 'Sort: Years of experience' },
  { value: 'name', label: 'Sort: Name (A–Z)' },
]

function SignalPips({ c }: { c: Candidate }) {
  const { present, total } = signalCounts(c)
  return (
    <div className="signal-dots">
      <div className="signal-dots__pips" aria-hidden>
        {c.signals.map((s) => (
          <span
            key={s.id}
            className={`pip${s.present ? ` is-on t${s.tier}` : ''}`}
            title={`${s.label}: ${s.present ? 'present' : 'not on file'}`}
          />
        ))}
      </div>
      <span className="signal-dots__count">
        {present}/{total} signals
      </span>
    </div>
  )
}

function CandidateCard({ c }: { c: Candidate }) {
  const nav = useNavigate()
  const { unlock, allowanceUsed, allowanceTotal, session } = useStore()
  const atCap = allowanceUsed >= allowanceTotal
  const canAct = canActOnPipeline(session.recruiter.seatRole)

  return (
    <Card hover className={`cand-card${c.unlocked ? '' : ' cand-card--locked'}`} onClick={() => nav(`/candidates/${c.id}`)}>
      <div className="cand-card__top">
        <Avatar hue={c.avatarHue} initials={c.initials} size={46} />
        <div className="cand-card__id">
          <div className="cand-card__name">{c.name}</div>
          <div className="cand-card__headline">{c.headline}</div>
          <div className="cand-card__meta" style={{ marginTop: '0.35rem' }}>
            <IconPin size={13} /> {c.location}
            <span className="dot-sep" />
            {c.yearsExperience} yrs
            {isThinCredential(c) && (
              <>
                <span className="dot-sep" />
                <span className="thin-chip" title="Newly created with little corroboration (§12.3)">
                  Thin
                </span>
              </>
            )}
            {c.riskFlags?.map((f) => (
              <Fragment key={f.id}>
                <span className="dot-sep" />
                <span className={`risk-chip risk-chip--${f.severity}`} title={f.detail}>
                  {f.label}
                </span>
              </Fragment>
            ))}
          </div>
        </div>
        <GhostMark size={26} tint={c.state} breathing={c.state === 'live'} title={`Tier ${c.tier}`} />
      </div>

      <div className="pill-row">
        {c.topSkills.slice(0, 3).map((s) => (
          <span key={s} className="skill-pill">
            {s}
          </span>
        ))}
      </div>

      <SignalPips c={c} />

      <div className="cand-card__foot">
        {c.unlocked ? (
          <>
            <CredentialBadge state={c.state} />
            <span className="cand-card__active">Active {timeAgo(c.lastActive)}</span>
          </>
        ) : (
          <>
            <span className="lock-chip">
              <IconLock size={13} /> Locked
            </span>
            <Button
              size="sm"
              variant="primary"
              icon={<IconLock size={14} />}
              disabled={atCap || !canAct}
              title={!canAct ? 'Viewers have read-only access' : atCap ? 'Monthly allowance reached' : undefined}
              onClick={(e) => {
                e.stopPropagation()
                unlock(c.id)
              }}
            >
              Unlock
            </Button>
          </>
        )}
      </div>
    </Card>
  )
}

function StatCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon?: ReactNode }) {
  return (
    <Card inset className="stat">
      <div className="stat__label">
        {icon}
        {label}
      </div>
      <div className="stat__value">{value}</div>
      {sub && <div className="stat__sub">{sub}</div>}
    </Card>
  )
}

export function Candidates() {
  const { candidates } = useStore()
  const loading = useBriefLoad()
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<SortKey>('verification')

  const stats = useMemo(() => {
    const tier3 = candidates.filter((c) => c.tier === 3).length
    const live = candidates.filter((c) => c.state === 'live').length
    const unlocked = candidates.filter((c) => c.unlocked).length
    return { total: candidates.length, tier3, live, unlocked }
  }, [candidates])

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const matched = candidates.filter((c) => {
      if (!needle) return true
      return (
        c.name.toLowerCase().includes(needle) ||
        c.headline.toLowerCase().includes(needle) ||
        c.location.toLowerCase().includes(needle) ||
        c.topSkills.some((s) => s.toLowerCase().includes(needle))
      )
    })
    const sorted = [...matched]
    switch (sort) {
      case 'verification':
        sorted.sort((a, b) => verificationScore(b) - verificationScore(a))
        break
      case 'recent':
        sorted.sort((a, b) => +new Date(b.lastActive) - +new Date(a.lastActive))
        break
      case 'experience':
        sorted.sort((a, b) => b.yearsExperience - a.yearsExperience)
        break
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name))
        break
    }
    return sorted
  }, [candidates, q, sort])

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Candidates</h1>
          <p className="page__sub">Your verified-talent pool. Verification surfaces strong candidates — it never hides anyone.</p>
        </div>
      </div>

      <div className="pool-stats">
        <StatCard label="In your pool" value={String(stats.total)} icon={<GhostMark size={15} />} />
        <StatCard label="Deeply verified" value={String(stats.tier3)} sub="Tier 3 · end-to-end" icon={<IconShield size={14} />} />
        <StatCard label="Live now" value={String(stats.live)} sub="Fresh verification" icon={<IconCheck size={14} />} />
        <StatCard label="Unlocked" value={String(stats.unlocked)} sub="Full detail revealed" icon={<IconLock size={14} />} />
      </div>

      <div className="toolbar">
        <div className="toolbar__search">
          <IconSearch size={18} />
          <input
            className="input"
            placeholder="Search name, role, skill, or location…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="toolbar__sort">
          <Select
            options={SORTS}
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Sort candidates"
          />
        </div>
      </div>

      <div className="toolbar__note">
        <IconShield size={14} />
        Verification is a positive signal, not a filter — every candidate stays visible; the strongest simply rise first.
      </div>

      {loading ? (
        <div className="cand-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="cand-card">
              <div className="cand-card__top">
                <Skeleton w={46} h={46} r={23} />
                <div style={{ flex: 1 }}>
                  <Skeleton w="60%" h={16} />
                  <Skeleton w="80%" h={12} style={{ marginTop: 8 }} />
                </div>
              </div>
              <Skeleton w="100%" h={28} r={10} />
              <Skeleton w="40%" h={12} />
            </Card>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState title="No candidates match" body={`Nothing matches “${q}”. Try a different name, role, or skill.`} />
      ) : (
        <div className="cand-grid stagger">
          {visible.map((c) => (
            <CandidateCard key={c.id} c={c} />
          ))}
        </div>
      )}

      {/* keep TierBadge import meaningful for legend below */}
      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="section-title" style={{ margin: 0 }}>Trust ladder</span>
        <TierBadge tier={1} state="passive" />
        <TierBadge tier={2} state="vault" />
        <TierBadge tier={3} state="live" />
      </div>
    </div>
  )
}
